
import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CategoryGroupProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  onOpenChange?: (isOpen: boolean) => void;
}

const CategoryGroup: React.FC<CategoryGroupProps> = ({ 
  title, 
  children, 
  defaultOpen = false,
  className,
  onOpenChange
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  
  React.useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);
  
  const toggleOpen = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (onOpenChange) {
      onOpenChange(newIsOpen);
    }
  };

  return (
    <div className={cn("mb-4", className)}>
      <div 
        className="flex items-center justify-between cursor-pointer mb-2 group"
        onClick={toggleOpen}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={`category-content-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <h4 className="text-sm font-medium text-foreground/80 group-hover:text-foreground transition-colors">
          {title}
        </h4>
        <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Button>
      </div>
      
      <div 
        id={`category-content-${title.toLowerCase().replace(/\s+/g, '-')}`}
        className={cn(
          "grid transition-all duration-300 ease-in-out",
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 overflow-hidden"
        )}
      >
        <div className="min-h-0 overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CategoryGroup;
